import React from 'react';

import { DUPLICATE_STEP_IDS_MESSAGE } from 'constants/uiConstants';
import type { IInputTask } from 'interfaces/IInputTask';
import MergeClassNames from 'utils/MergeClassNames';

import AddInputControl from './AddInputControl';
import { hasDuplicateStepIds } from './hasDuplicateStepIds';
import { getTaskSteps } from './stepCalculations';
import { getInputsHiddenByFilterMessage } from './stepCategoryFilters';
import { StepSelection } from './stepFactory';

interface InputTableFooterProps {
  task: IInputTask;
  hiddenCount: number;
  addInputCategoryKey?: string;
  isAddInputOpen: boolean;
  onAddInputOpenChange: (open: boolean) => void;
  onAddInputClose: () => void;
  onAddInputSelect: (selections: StepSelection[]) => void;
  enableMultiSelect?: boolean;
  addInputDisabledReason?: string;
}

// Sits below a task's step sub-table: the "+ Add" control for another input,
// how many steps the current category filter is hiding, and — on a task
// whose step ids repeat — the warning that re-ordering/editing/deleting are
// unavailable for it.
const InputTableFooter: React.FC<InputTableFooterProps> = ({
  task,
  hiddenCount,
  addInputCategoryKey,
  isAddInputOpen,
  onAddInputOpenChange,
  onAddInputClose,
  onAddInputSelect,
  enableMultiSelect,
  addInputDisabledReason,
}) => {
  const footerHasAmbiguous =
    getTaskSteps(task).length > 1 && hasDuplicateStepIds(getTaskSteps(task));
  return (
    <div
      className={MergeClassNames(
        'flex items-center gap-2 border-l-2 px-2 pb-4 pt-2',
        footerHasAmbiguous ? 'border-danger-dark' : 'border-success-dark'
      )}
    >
      <AddInputControl
        task={task}
        addInputCategoryKey={addInputCategoryKey}
        isOpen={isAddInputOpen}
        onOpenChange={onAddInputOpenChange}
        onClose={onAddInputClose}
        onSelect={onAddInputSelect}
        enableMultiSelect={enableMultiSelect}
        disabledReason={addInputDisabledReason}
      />
      {hiddenCount > 0 && (
        <span className="text-sm text-gray-dark">
          {getInputsHiddenByFilterMessage(hiddenCount)}
        </span>
      )}
      {/* Sits beside the add control rather than over the grid: the handles it
        explains are gone, so there is nothing up there to point at. Only
        worth saying where re-ordering would otherwise have been on offer —
        a task with one step never shows handles anyway, so the warning
        would read as unexplained noise. */}
      {footerHasAmbiguous && (
        <span className="text-sm text-danger-dark">
          {DUPLICATE_STEP_IDS_MESSAGE}
        </span>
      )}
    </div>
  );
};

export default InputTableFooter;
